import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import {
  Setting,
  SettingEvent,
  settingEventFromJSON,
  SettingEventType,
} from '@repo/sdk/models/components';

import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useStream } from '@/hooks/use-stream';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { settingsStreamQuery } from '@/queries/gaia-api/settings-stream-query';
import { STREAM_TYPES } from '@/state/stream-status.state';

// build a settings family atom so that we can use it to pull out settings by settingId
export const settingsFamily = atomFamily(
  (setting: Setting) => atom(setting),
  (a: Setting, b: Setting) => a.settingId === b.settingId
);

export type SubscriptionEvent = {
  setting_ids: string[];
  kill?: boolean;
};
/* this hook is responsible for opens the websockets and managing the streamed events */
export const useSettingsStream = () => {
  const { organizationId, agentId } = useCurrentFacility();

  const queryClient: QueryClient = useQueryClient();
  const { webSocketUrl, enabled, queryKey } = settingsStreamQuery({ agentId });

  const handleSettingsEvent = useCallback(
    (event: SettingEvent) => {
      switch (event.type) {
        case SettingEventType.Keepalive: {
          // Keep Alive is a keep alive message.
          // Maybe: A single keepalive cache for all sockets?
          console.debug(`Settings keep alive at ${new Date().toISOString()}`);
          break;
        }
        case SettingEventType.Setting: {
          // Update the settings query data and the jotai store
          event?.settings?.forEach((setting) => {
            jotaiStore.set(settingsFamily(setting), setting);
            const settingQueryKey = queryKey.concat(setting.settingId);

            // Find all queries that start with this settingQueryKey pattern
            const matchingQueries = queryClient.getQueryCache().findAll({
              predicate: (query) => {
                const key = query.queryKey as string[];
                // Check if the query key starts with our settingQueryKey
                return (
                  key.length >= settingQueryKey.length &&
                  settingQueryKey.every((segment, index) => segment === key[index])
                );
              },
            });

            // Update all matching queries
            matchingQueries.forEach((query) => {
              queryClient.setQueryData(query.queryKey, (old: any) => ({
                ...old,
                setting: setting,
                subscribed: true,
              }));
            });
          });
          break;
        }
        default:
          break;
      }
    },
    [queryClient, organizationId, agentId]
  );
  return useStream<SettingEvent, SubscriptionEvent>(enabled ? webSocketUrl : null, {
    onReceiveContent: handleSettingsEvent,
    parse: settingEventFromJSON,
    streamType: STREAM_TYPES.SETTINGS,
    displayName: 'Settings',
  });
};
